import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { GoogleMap, LoadScript, Polyline, Marker } from '@react-google-maps/api';
import { Paper, Alert } from '@mui/material';
import apiService from '../../../services/api';
import CustomLoading from '../../../components/common/CustomLoading';
import { getVehicleIconDataUri, getStartPointIconDataUri } from '../../../assets/icons/VehicleIcon';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDXGybGe_ytlLhIMAfKkAC87FojSPS9ghk';

interface TelematicsDataPoint {
  latitude: number;
  longitude: number;
  odometer?: number;
  timestamp?: string;
  created_at?: string;
}

interface RideRouteMapProps {
  bookingId: string;
}

const RideRouteMap: React.FC<RideRouteMapProps> = ({ bookingId }) => {
  const [data, setData] = useState<TelematicsDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (!bookingId) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = (await apiService.getTelematicsDeviceData(bookingId)) as
          | TelematicsDataPoint[]
          | { data?: TelematicsDataPoint[] };

        const apiData = Array.isArray(response)
          ? response
          : Array.isArray((response as any).data)
          ? (response as any).data
          : [];

        const validData = apiData.filter(
          (p: TelematicsDataPoint) =>
            typeof p.latitude === 'number' &&
            typeof p.longitude === 'number' &&
            !isNaN(p.latitude) &&
            !isNaN(p.longitude)
        );

        setData(validData);
      } catch {
        setError('Failed to fetch telematics data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [bookingId]);

  const path = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      const t1 = new Date(a.timestamp || a.created_at || '').getTime();
      const t2 = new Date(b.timestamp || b.created_at || '').getTime();
      return t1 - t2;
    });
    return sorted.map((p) => ({ lat: p.latitude, lng: p.longitude }));
  }, [data]);

  const mapCenter = useMemo(() => {
    if (!path.length) return { lat: 19.128525, lng: 72.925219 };
    if (path.length === 1) return path[0];
    const avgLat = path.reduce((sum, p) => sum + p.lat, 0) / path.length;
    const avgLng = path.reduce((sum, p) => sum + p.lng, 0) / path.length;
    return { lat: avgLat, lng: avgLng };
  }, [path]);

  const startMarker = useMemo(() => {
    if (!path.length) return null;
    return path[0];
  }, [path]);

  const endMarker = useMemo(() => {
    if (!path.length) return null;
    return path[path.length - 1];
  }, [path]);

  const vehicleRotation = useMemo(() => {
    if (path.length < 2 || !window.google?.maps) return 0;
    
    const lastPoint = path[path.length - 1];
    const secondLastPoint = path[path.length - 2];
    
    const lat1 = (secondLastPoint.lat * Math.PI) / 180;
    const lat2 = (lastPoint.lat * Math.PI) / 180;
    const deltaLng = ((lastPoint.lng - secondLastPoint.lng) * Math.PI) / 180;
    
    const y = Math.sin(deltaLng) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
    
    const bearing = (Math.atan2(y, x) * 180) / Math.PI;
    return (bearing + 360) % 360;
  }, [path]);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      if (!path.length || !window.google?.maps) return;

      const bounds = new window.google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(new window.google.maps.LatLng(p.lat, p.lng)));

      const allSame = path.every((p) => p.lat === path[0].lat && p.lng === path[0].lng);

      if (allSame) {
        map.setCenter(new window.google.maps.LatLng(path[0].lat, path[0].lng));
        map.setZoom(17);
      } else {
        map.fitBounds(bounds, 50);
        const listener = window.google.maps.event.addListener(map, 'bounds_changed', () => {
          const currentZoom = map.getZoom();
          if (currentZoom && currentZoom > 18) {
            map.setZoom(18);
          } else if (currentZoom && currentZoom < 13) {
            map.setZoom(13);
          }
          window.google.maps.event.removeListener(listener);
        });
      }
    },
    [path]
  );

  const startPointIcon = useMemo(() => {
    if (!mapsLoaded || !window.google?.maps || !startMarker) return undefined;
    
    const iconSize = 30;
    const iconHeight = iconSize * 1.67;
    const dataUri = getStartPointIconDataUri(iconSize);
    
    const icon = {
      url: dataUri,
      scaledSize: new window.google.maps.Size(iconSize, iconHeight),
      anchor: new window.google.maps.Point(iconSize / 2, iconHeight),
    } as google.maps.Icon;
    
    return icon;
  }, [mapsLoaded, startMarker]);

  const vehicleIcon = useMemo(() => {
    if (!mapsLoaded || !window.google?.maps || !endMarker) return undefined;
    
    const iconSize = 48;
    const dataUri = getVehicleIconDataUri(vehicleRotation, iconSize);
    
    const icon = {
      url: dataUri,
      scaledSize: new window.google.maps.Size(iconSize, iconSize),
      anchor: new window.google.maps.Point(iconSize / 2, iconSize / 2),
      rotation: 0,
    } as google.maps.Icon;
    
    return icon;
  }, [mapsLoaded, vehicleRotation, endMarker]);

  if (loading) {
    return (
      <Paper sx={{ p: 3, height: 'calc(100vh - 150px)' }}>
        <CustomLoading message="Loading route data..." />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, height: 'calc(100vh - 150px)' }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!data.length) {
    return (
      <Paper sx={{ p: 3, height: 'calc(100vh - 150px)' }}>
        <Alert severity="info">No telematics data available for this ride.</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: 'calc(100vh - 150px)' }}>
      {!mapsLoaded && <CustomLoading message="Loading map..." />}
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} onLoad={() => setMapsLoaded(true)}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%', borderRadius: '12px' }}
          center={mapCenter}
          zoom={15}
          onLoad={onMapLoad}
          options={{
            streetViewControl: false,
            zoomControl: true,
            mapTypeControl: false,
            fullscreenControl: false,
            disableDefaultUI: false,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'transit',
                stylers: [{ visibility: 'off' }],
              },
            ],
          }}
        >
          {mapsLoaded && path.length > 0 && window.google?.maps && (
            <Polyline
              path={path}
              options={{
                strokeColor: '#2196F3',
                strokeWeight: 2,
                strokeOpacity: 1,
                geodesic: true,
                zIndex: 1,
              }}
            />
          )}

          {mapsLoaded && startMarker && window.google?.maps && startPointIcon && (
            <Marker
              position={startMarker}
              icon={startPointIcon}
              zIndex={9}
              title="Start Point"
            />
          )}

          {mapsLoaded && endMarker && window.google?.maps && vehicleIcon && (
            <Marker
              position={endMarker}
              icon={vehicleIcon}
              zIndex={10}
              title="Vehicle Location"
            />
          )}
        </GoogleMap>
      </LoadScript>
    </Paper>
  );
};

export default RideRouteMap;
